import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/Button";
import { FormCard } from "@/components/FormCard";
import { resetPasswordSchema } from "@/model/authSchema";
import { resetPassword } from "aws-amplify/auth";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useState } from "react";

type TResetPasswordSchema = z.infer<typeof resetPasswordSchema>;

type ResetPasswordProps = {
  handleSuccess?: () => void;
  email?: string;
};

export default function ResetPassword({
  handleSuccess,
  email,
}: ResetPasswordProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const form = useForm<TResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: email || "",
    },
  });

  const onSubmit = async (data: TResetPasswordSchema) => {
    const { email } = data;

    try {
      await resetPassword({ username: email });
      toast.success(
        "An email has been sent to your inbox with instructions to reset your password."
      );

      const redirect = searchParams.get("redirect");
      const params = new URLSearchParams({ email });
      if (redirect) {
        params.set("redirect", redirect);
      }

      navigate(`/forgot-password?${params.toString()}`);
      handleSuccess?.();
    } catch (error: any) {
      const errorType = error.code || error.name || error.__type;
      if (errorType === "UserNotFoundException") {
        form.setError("email", {
          type: "manual",
          message: "No account found with this email address",
        });
      } else if (errorType === "LimitExceededException") {
        form.setError("email", {
          type: "manual",
          message: "Attempt limit exceeded. Please try again later.",
        });
      } else if (errorType === "InvalidParameterException") {
        form.setError("email", {
          type: "manual",
          message:
            "The email address does not appear to be attached to a valid SolarCloud account.",
        });
        toast.error("Please verify your email address.");
        navigate(
          `/verify?redirect=/reset-password&email=${encodeURIComponent(email)}`
        );
      } else {
        form.setError("email", {
          type: "manual",
          message: `Something went wrong. Please try again. ${errorType}`,
        });
      }
    }
  };

  return (
    <FormCard
      form={form}
      onSubmit={onSubmit}
      title="Forgotten Password"
      description="Enter your email to reset your password."
    >
      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input placeholder="you@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <Button
        disabled={form.formState.isSubmitting || !form.formState.isValid}
        type="submit"
      >
        {form.formState.isSubmitting ? "Sending..." : "Reset Password"}
      </Button>
      <div className="text-center text-sm">
        <Button variant="link" className="hover:underline">
          <Link to="/login">Back to Sign In</Link>
        </Button>
      </div>
    </FormCard>
  );
}
